import { mapLocalToMedia, mapMediaToLocal, speedAtLocal } from './retime'
import { getStripRetimePointsLocal, isStripRetimeEnabled } from './retimePoints'

const clamp = (value, min, max) => {
  if (!Number.isFinite(value)) return min
  if (value <= min) return min
  if (value >= max) return max
  return value
}

const asNumber = (value, fallback = 0) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : fallback
}

function makeIdentityMap() {
  return {
    start: 0,
    length: 0,
    sourceStart: 0,
    timeScale: 1,
    retimeEnabled: false,
    hasPoints: false,
    usesCurve: false,
    toLocalTime: () => 0,
    localToMedia: (localSec) => Math.max(0, Number(localSec) || 0),
    mediaToLocal: (mediaSec) => Math.max(0, Number(mediaSec) || 0),
    localToSourceMedia: (localSec) => Math.max(0, Number(localSec) || 0),
    sourceMediaToLocal: (mediaSec) => Math.max(0, Number(mediaSec) || 0),
    toMediaTime: (globalSec) => Math.max(0, Number(globalSec) || 0),
    toTimelineTime: (mediaSec) => Math.max(0, Number(mediaSec) || 0),
    instantaneousRate: () => 1,
    localRate: () => 1,
  }
}

export function createStripRetimeMap(strip) {
  if (!strip) return makeIdentityMap()

  const length = Math.max(0, asNumber(strip.length, 0))
  const start = asNumber(strip.start, 0)
  const retime = strip.retime || {}
  const retimeEnabled = isStripRetimeEnabled(strip)
  const mode = typeof retime.mode === 'string' ? retime.mode : 'linear'
  const points = getStripRetimePointsLocal(strip, length)
  const hasPoints = points.length > 0
  const usesCurve = retimeEnabled && hasPoints
  const sourceStart = Math.max(0, asNumber(strip.sourceStart, 0))
  const timeScaleRaw = asNumber(strip.timeScale, 1)
  const legacyTimeScale = timeScaleRaw > 0 ? timeScaleRaw : 1
  const sourceDurationRaw = asNumber(strip.sourceDuration, NaN)
  const sourceDuration = sourceDurationRaw > 0
    ? sourceDurationRaw
    : Math.max(0, length * legacyTimeScale)
  const derivedTimeScale = length > 0 ? (sourceDuration / length) : legacyTimeScale
  const timeScale = derivedTimeScale > 0 ? derivedTimeScale : legacyTimeScale

  const clampLocal = (localSec) => clamp(asNumber(localSec, 0), 0, length)

  const localToMedia = (localSec) => {
    const local = clampLocal(localSec)
    if (usesCurve) return mapLocalToMedia(local, length, points, mode)
    return local * timeScale
  }

  const mediaToLocal = (mediaSec) => {
    const media = Math.max(0, asNumber(mediaSec, 0))
    if (usesCurve) return clampLocal(mapMediaToLocal(media, length, points, mode))
    const denom = timeScale > 0 ? timeScale : 1
    return clampLocal(media / denom)
  }

  const localToSourceMedia = (localSec) => sourceStart + localToMedia(localSec)

  const sourceMediaToLocal = (mediaSec) => {
    const sourceMedia = Math.max(sourceStart, asNumber(mediaSec, sourceStart))
    return mediaToLocal(Math.max(0, sourceMedia - sourceStart))
  }

  const toLocalTime = (globalSec) => clampLocal(asNumber(globalSec, 0) - start)

  const toMediaTime = (globalSec) => localToSourceMedia(toLocalTime(globalSec))

  const toTimelineTime = (mediaSec) => start + sourceMediaToLocal(mediaSec)

  const localRate = (localSec) => {
    // Keep rate derived from the same map used by audio/video seeking.
    if (usesCurve) return speedAtLocal(clampLocal(localSec), length, points, mode)
    return timeScale
  }

  const instantaneousRate = (globalSec) => localRate(toLocalTime(globalSec))

  return {
    start,
    length,
    sourceStart,
    sourceDuration,
    timeScale,
    retimeEnabled,
    hasPoints,
    usesCurve,
    mode,
    points,
    toLocalTime,
    localToMedia,
    mediaToLocal,
    localToSourceMedia,
    sourceMediaToLocal,
    toMediaTime,
    toTimelineTime,
    localRate,
    instantaneousRate,
  }
}

export function ensureStripRetimeMap(strip, existing) {
  if (!existing) return createStripRetimeMap(strip)
  if (!strip) return existing

  const length = Math.max(0, asNumber(strip.length, 0))
  const start = asNumber(strip.start, 0)
  const retimeEnabled = isStripRetimeEnabled(strip)
  const mode = typeof (strip.retime && strip.retime.mode) === 'string' ? strip.retime.mode : 'linear'
  const points = getStripRetimePointsLocal(strip, length)
  const hasPoints = points.length > 0
  const usesCurve = retimeEnabled && hasPoints
  const sourceStart = Math.max(0, asNumber(strip.sourceStart, 0))
  const timeScaleRaw = asNumber(strip.timeScale, 1)
  const legacyTimeScale = timeScaleRaw > 0 ? timeScaleRaw : 1
  const sourceDurationRaw = asNumber(strip.sourceDuration, NaN)
  const sourceDuration = sourceDurationRaw > 0
    ? sourceDurationRaw
    : Math.max(0, length * legacyTimeScale)
  const derivedTimeScale = length > 0 ? (sourceDuration / length) : legacyTimeScale
  const timeScale = derivedTimeScale > 0 ? derivedTimeScale : legacyTimeScale

  const needsRefresh = (
    existing.start !== start ||
    existing.length !== length ||
    existing.sourceStart !== sourceStart ||
    existing.sourceDuration !== sourceDuration ||
    existing.timeScale !== timeScale ||
    existing.retimeEnabled !== retimeEnabled ||
    existing.mode !== mode ||
    existing.hasPoints !== hasPoints ||
    existing.usesCurve !== usesCurve ||
    points.some((p, idx) => {
      const prev = existing.points?.[idx]
      return !prev || prev.t !== p.t || prev.speed !== p.speed || prev.affects !== p.affects
    }) ||
    (existing.points && existing.points.length !== points.length)
  )

  return needsRefresh ? createStripRetimeMap(strip) : existing
}


